import { v4 as uuidv4 } from 'uuid';
import { z } from 'zod';
import { zPrice, zText, zTextNullable } from '../primitives';

export const menuItemSchema = z.object({
  categoryUuid: z.string(), // Note: Don't use `categorySchema.shape.uuid` as it uses default value which makes input type optional
  comesWithModifiersUuids: z.array(z.string()).default([]),
  description: z.string().nullable().default(null),
  displayIndex: z.number().nullable().default(null),
  isGstExempt: z.boolean().default(false),
  isVisiblePos: z.boolean().default(true),
  isVisibleWeb: z.boolean().default(true),
  modifierGroupUuids: z.array(z.string()).default([]),
  name: z.string(),
  /** If `sizeModifierGroupId` is used, `price` should be set to `null`. */
  price: z.number().nullable().default(null),
  sizeModifierGroupUuid: z.string().nullable().default(null),
  uuid: z.string().default(() => uuidv4()),
  cdnImageKey: z.string().nullable().default(null).optional(), // Remove optional() later

  /** `createdAt` is needed because `_creationTime` is only set on the root level object. */
  createdAt: z.number().optional(), // `createdAt` is needed so menuItemSchema can still pass ok on server side
  updatedAt: z.number().optional(), // `updatedAt` is needed for highlighting changes
});
export type MenuItem = z.output<typeof menuItemSchema>;

/** Strict schema */
export const menuItemStrictSchema = menuItemSchema
  .safeExtend({
    categoryUuid: z.guid(),
    comesWithModifiersUuids: z.array(z.guid()),
    description: zTextNullable,
    displayIndex: z.int().nullable(),
    modifierGroupUuids: z.array(z.guid()),
    name: zText,
    price: zPrice.nullable(),
    sizeModifierGroupUuid: z.guid().nullable(),
    uuid: z.guid(),
  })
  /** Validate: `price` OR `sizeModifierGroupUuid` must be set. */
  .superRefine((menuItem, ctx) => {
    if (menuItem.price === null && !menuItem.sizeModifierGroupUuid) {
      ctx.addIssue({
        code: 'custom',
        message: 'Price must be set, or a size modifier chosen.',
        path: ['price'],
      });
    }
    /** Validate: Don't allow both values to be set */
    if (menuItem.price !== null && menuItem.sizeModifierGroupUuid) {
      ctx.addIssue({
        code: 'custom',
        message: 'Price OR size modifier should be set, but not both.',
        path: ['price'],
      });
    }
  });

/** Convex schema */
export const menuItemCvxSchema = menuItemSchema.safeExtend({
  createdAt: z.number(),
  updatedAt: z.number(),
});
export type MenuItemCvx = z.output<typeof menuItemCvxSchema>;

/**
 * For POS, we return as a Record for better performance.
 * `undefined` is added because accessing using `menuItems[categoryId]` may return undefined
 * if the category has no menu items. This isn't handled by default by TS.
 */
export const menuItemsByCategorySchema = z.record(
  z.string(),
  z.array(menuItemCvxSchema).nullable(),
);
export type MenuItemsByCategory = z.output<typeof menuItemsByCategorySchema>;
